import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type FormEvent,
  type FunctionComponent,
  type KeyboardEvent,
} from 'react';
import { useCheckoutPricing } from '@recurly/react-recurly';
import classNames from 'classnames';
import { Button, Spinner } from '@fff-web/fff-ui-shared';
import { Address, Adjustment } from '../../../../interfaces';
import { type ICouponInputV2 } from '../../../../interfaces/ICouponInput';
import { CTA_APPLY_COUPON } from '../../../utils/buttonCTA';
import { FieldError } from '../../Shared';
import { CI_FORM, CI_INPUT, CI_INPUT_WRAPPER, CI_LABEL } from '../CouponInput/couponInputTestIds';
import RecurlyWrapper from '../RecurlyWrapper';
import { RecurlyError } from '../utils';
import * as styles from './CouponInputV2.module.css';

const adjustments: Adjustment[] = [];
const PLACEHOLDER = 'Enter Promo Code or Gift Card';

const address: Address = {
  first_name: '',
  last_name: '',
  country: '',
  postal_code: '',
};

const shippingAddress: Address = {
  first_name: '',
  last_name: '',
  country: '',
  postal_code: '',
};

interface FormData {
  couponCode: string;
}

export const CouponInputWrapped: FunctionComponent<ICouponInputV2> = ({
  coupon,
  initialCouponCode,
  handleOnChange,
  handleReturnPrice,
  placeholder,
  plan,
  variant = 'default',
}) => {
  const [recurlyError, setRecurlyError] = useState<RecurlyError | null>(null);
  const formDataRef = useRef<FormData>({ couponCode: '' });
  const [{ price: recurlyPrice, loading }, setPricing] = useCheckoutPricing({}, setRecurlyError);
  const isDefault = variant === 'default';

  useEffect(() => {
    if (!loading) {
      handleReturnPrice(recurlyPrice);
    }
  }, [loading]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    if (initialCouponCode) {
      timer = setTimeout(() => {
        onSubmitRecurly(initialCouponCode);
      }, 1000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [initialCouponCode]);

  const onSubmitRecurly = (value: string) => {
    setPricing({
      coupon: value,
      subscriptions: plan ? [{ plan }] : [],
      adjustments,
      address,
      shippingAddress,
    });
  };

  const handleInputBlur = (e: FocusEvent<HTMLInputElement>) => {
    e.preventDefault();
    onSubmitRecurly(e.target.value);
    handleOnChange(e.target.value);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setRecurlyError(null);
    handleOnChange(e.target.value);
    const { name, value } = e.target;
    formDataRef.current[name as keyof FormData] = value;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSubmitRecurly(formDataRef.current.couponCode);
  };

  const onKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSubmitRecurly(formDataRef.current.couponCode);
    }
  };

  const invalidInputStyles = recurlyError ? ` ${styles.ciInvalid}` : '';

  return (
    <form data-testid={CI_FORM} onSubmit={handleSubmit} className={styles.ciContainer}>
      <label data-testid={CI_LABEL} htmlFor="couponCode" className={styles.ciLabel}>
        Enter Promo Code
        <div
          data-testid={CI_INPUT_WRAPPER}
          className={classNames({
            [styles.ciInputWrapper]: isDefault,
            [styles.secondaryInputWrapper]: !isDefault,
          })}
        >
          <input
            data-testid={CI_INPUT}
            className={`${styles.ciInput}${invalidInputStyles}`}
            type="text"
            name="couponCode"
            onChange={handleInputChange}
            defaultValue={initialCouponCode ?? ''}
            value={coupon}
            disabled={loading}
            placeholder={placeholder ?? PLACEHOLDER}
            onBlur={handleInputBlur}
            onKeyPress={onKeyPress}
          />
          <Button
            type="submit"
            disabled={loading}
            variation={isDefault ? 'primary-outline' : 'primary'}
          >
            {isDefault ? CTA_APPLY_COUPON : <Spinner />}
          </Button>
        </div>
        {recurlyError && <FieldError errorMsg="This promo code is invalid" />}
      </label>
    </form>
  );
};

const CouponInput: FunctionComponent<ICouponInputV2> = ({
  coupon,
  initialCouponCode,
  handleOnChange,
  plan,
  handleReturnPrice,
  placeholder,
  variant,
}) => (
  <RecurlyWrapper>
    <CouponInputWrapped
      {...{
        coupon,
        initialCouponCode,
        handleOnChange,
        plan,
        handleReturnPrice,
        placeholder,
        variant,
      }}
    />
  </RecurlyWrapper>
);

export default CouponInput;
